import Debug from '../utils/Debug.js';

/**
 * JoinOverlay — Full-screen loading overlay shown during multiplayer room join.
 * Displays a spinner, status text, diagnostic log, and cancel button.
 */
export default class JoinOverlay {
  constructor() {
    this._el = null;
    this._statusEl = null;
    this._spinnerEl = null;
    this._logEl = null;
    this._cancelBtn = null;
    this._onCancel = null;
    this._logLines = [];
  }

  /**
   * Show the overlay with a spinner and cancel button.
   * @param {function} onCancel - Called when user clicks cancel
   */
  show(onCancel) {
    this._onCancel = onCancel;

    this._el = document.createElement('div');
    this._el.className = 'join-overlay';

    const content = document.createElement('div');
    content.className = 'join-overlay-content';

    // Spinner
    this._spinnerEl = document.createElement('div');
    this._spinnerEl.className = 'join-overlay-spinner';
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('div');
      dot.className = 'join-overlay-dot';
      this._spinnerEl.appendChild(dot);
    }
    content.appendChild(this._spinnerEl);

    // Status text
    this._statusEl = document.createElement('div');
    this._statusEl.className = 'join-overlay-status';
    this._statusEl.textContent = 'Connecting...';
    content.appendChild(this._statusEl);

    // Diagnostic log (visible on all devices for debugging)
    this._logEl = document.createElement('div');
    this._logEl.className = 'join-overlay-log';
    content.appendChild(this._logEl);

    // Cancel button
    this._cancelBtn = document.createElement('button');
    this._cancelBtn.className = 'gui-button gui-button-danger';
    this._cancelBtn.textContent = 'Cancel';
    this._cancelBtn.addEventListener('click', () => this._onCancel?.());
    content.appendChild(this._cancelBtn);

    this._el.appendChild(content);
    document.body.appendChild(this._el);
  }

  /**
   * Update the status text.
   * @param {string} text
   */
  setStatus(text) {
    if (this._statusEl) this._statusEl.textContent = text;
  }

  /**
   * Add a line to the diagnostic log visible on the overlay.
   * @param {string} text
   */
  log(text) {
    const ts = ((performance.now() / 1000) | 0) + 's';
    const line = `[${ts}] ${text}`;
    this._logLines.push(line);
    Debug.log('JoinOverlay', text);
    if (this._logEl) {
      this._logEl.textContent = this._logLines.slice(-12).join('\n');
      this._logEl.scrollTop = this._logEl.scrollHeight;
    }
  }

  /**
   * Show an error state — hides spinner, shows red error text.
   * @param {string} message
   */
  showError(message) {
    if (this._spinnerEl) this._spinnerEl.style.display = 'none';
    if (this._statusEl) {
      this._statusEl.className = 'join-overlay-error';
      this._statusEl.textContent = message;
    }
    if (this._cancelBtn) {
      this._cancelBtn.textContent = 'Close';
      // Replace cancel handler with direct dismiss — the join already failed,
      // so _onCancel (which calls _rejectJoin) is no longer valid.
      this._cancelBtn.onclick = () => this.hide();
    }
  }

  /**
   * Fade out and remove the overlay from the DOM.
   */
  hide() {
    if (!this._el) return;
    this._el.classList.add('fade-out');
    const el = this._el;
    this._el = null;
    setTimeout(() => el.remove(), 300);
  }
}
